import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { indexStore } from "../../../../store/indexStore";
import { Button } from "../../../ui/button";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { Form, FormField } from "src/Components/ui/form";
import MenuTabs from "../../menus/MenuTabs";
import FormFieldItem from "../../../../common/form/FormFieldItem";
import { DialogClose } from "@radix-ui/react-dialog";
import { ComboboxForm } from "src/Components/selectbox/ComboBoxForm";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../ui/card";
import { FolderTree, UtensilsCrossed } from "lucide-react";
import { Separator } from "../../../ui/separator";

function CategoryForm(props) {
  const { categoryLoading, menus, nameLang } = indexStore();
  const { t } = useTranslation();

  const formSchema = z.object({
    name_ar: z
      .string({
        required_error: `${t("gloabl.name")} ${t(
          "errorMessage.fieldRequired"
        )}.`,
      })
      .min(2, {
        message: `${t("gloabl.name")} ${t("errorMessage.fieldCharsLength")}.`,
      }),
    name_en: z
      .string({
        required_error: `${t("gloabl.name")} ${t(
          "errorMessage.fieldRequired"
        )}.`,
      })
      .min(2, {
        message: `${t("gloabl.name")} ${t("errorMessage.fieldCharsLength")}.`,
      }),

    section_id: z.any({
      required_error: `${t("gloabl.menu")} ${t("errorMessage.fieldRequired")}.`,
    }),
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ...props.datasource,
    },
  });
  
  const dialogCloseRef = useRef();
  
  const onSubmit = (values) => {
    const submitValues = {
      ...values,
    };
    props.onSubmit(submitValues);
    if (dialogCloseRef.current) {
      dialogCloseRef.current.click();
    }
  };
  
  const onErorr = (errors) => {
    toast.error(t("errorMessage.fillAll"));
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit, onErorr)}
        className="space-y-6"
      >
        {/* Basic Information Card */}
        <Card className="border-2">
          <CardHeader className="pb-3 border-b">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <FolderTree className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">{t("gloabl.basic_information") || "Basic Information"}</CardTitle>
                <CardDescription>
                  {t("gloabl.basic_information_desc") || "Enter the category name in both languages"}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <MenuTabs form={form} className="bg-transparent" />
          </CardContent>
        </Card>

        {/* Menu Selection Card */}
        <Card className="border-2">
          <CardHeader className="pb-3 border-b">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <UtensilsCrossed className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">{t("gloabl.menu")}</CardTitle>
                <CardDescription>
                  {t("gloabl.select_menu_desc") || "Select the menu this category belongs to"}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <FormField
              control={form.control}
              name="section_id"
              render={({ field }) => {
                return (
                  <FormFieldItem required label={t("gloabl.menu")} form={form}>
                    <ComboboxForm
                      data={menus}
                      {...field}
                      placeholder={t("gloabl.select") + " " + t("gloabl.menu")}
                      lable={nameLang}
                    />
                  </FormFieldItem>
                );
              }}
            />
          </CardContent>
        </Card>

        <Separator />

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={categoryLoading}>
              {t("gloabl.cancel")}
            </Button>
          </DialogClose>
          <DialogClose ref={dialogCloseRef} />
          <Button type="submit" disabled={categoryLoading}>
            {categoryLoading ? t("gloabl.processing") || "Processing..." : t("gloabl.save")}
          </Button>
        </div>
      </form>
    </Form>
  );
}

export default CategoryForm;
